import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModuleOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusyOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { DashboardSummary } from '@/types/reports';

export default function OverviewPanel() {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const { data: processConfiguration } = useProcessConfiguration();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => (await apiClient.get<DashboardSummary>('/dashboard/summary')).data,
  });

  const cards = [
    { key: 'totalMaterials', label: t('totalMaterials'), value: data?.totalMaterials ?? 0, icon: <Inventory2Icon />, accentColor: '#4edea3' },
    {
      key: 'inventoryValue', label: t('inventoryValue'),
      value: `₹${(data?.totalStockValue ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, icon: <PaymentsIcon />, accentColor: '#adc6ff',
    },
    { key: 'pendingGrn', label: t('pendingGrn'), value: data?.pendingGrnCount ?? 0, icon: <MoveToInboxIcon />, accentColor: '#0566d9' },
    // Each hidden once its underlying step is switched off in Process Configuration — showing a
    // "Pending QC" count with no QC step in the process reads as broken, not zero.
    { key: 'pendingQc', label: t('pendingQc'), value: data?.pendingQcCount ?? 0, icon: <FactCheckIcon />, accentColor: '#ffb951', visible: processConfiguration ? processConfiguration.qcEnabled : true },
    { key: 'pendingPutaway', label: t('pendingPutaway'), value: data?.pendingPutawayCount ?? 0, icon: <ViewModuleIcon />, accentColor: '#bec6e0', visible: processConfiguration ? processConfiguration.putawayEnabled : true },
    { key: 'pendingPickLists', label: t('pendingPickLists'), value: data?.pendingPickListCount ?? 0, icon: <PlaylistAddCheckIcon />, accentColor: '#0566d9', visible: processConfiguration ? processConfiguration.dispatchEnabled : true },
    { key: 'lowStock', label: t('lowStock'), value: data?.lowStockCount ?? 0, icon: <WarningAmberIcon />, accentColor: '#ffb4ab' },
    { key: 'nearExpiry', label: t('nearExpiry'), value: data?.nearExpiryCount ?? 0, icon: <EventBusyIcon />, accentColor: '#ffb4ab' },
  ].filter((card) => card.visible !== false);

  const pendingQueueChart = useMemo(
    () => ({
      categories: [t('pendingGrn'), t('pendingQc'), t('pendingPutaway'), t('pendingPickLists')],
      series: [
        {
          label: t('overview.pendingItems'),
          data: [data?.pendingGrnCount ?? 0, data?.pendingQcCount ?? 0, data?.pendingPutawayCount ?? 0, data?.pendingPickListCount ?? 0],
          color: theme.palette.primary.main,
        },
      ],
    }),
    [data, t, theme],
  );

  const healthPie = useMemo(() => {
    const total = data?.totalMaterials ?? 0;
    const lowStock = data?.lowStockCount ?? 0;
    const nearExpiry = data?.nearExpiryCount ?? 0;
    const healthy = Math.max(total - lowStock - nearExpiry, 0);
    return [
      { label: t('overview.healthy'), value: healthy, color: theme.palette.success.main },
      { label: t('lowStock'), value: lowStock, color: theme.palette.warning.main },
      { label: t('nearExpiry'), value: nearExpiry, color: theme.palette.error.main },
    ];
  }, [data, t, theme]);

  const hasQueueData = pendingQueueChart.series[0].data.some((v) => v > 0);
  const hasHealthData = healthPie.some((d) => d.value > 0);

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        {cards.map((card) => (
          <GridItem key={card.label} xs={12} sm={6} md={3}>
            <StatCard label={card.label} value={card.value} icon={card.icon} accentColor={card.accentColor} />
          </GridItem>
        ))}
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('overview.pendingQueuesTitle')} subtitle={t('overview.pendingQueuesSubtitle')} loading={isLoading} empty={!isLoading && !hasQueueData}>
            <DashboardBarChart categories={pendingQueueChart.categories} series={pendingQueueChart.series} />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('overview.stockHealthTitle')} subtitle={t('overview.stockHealthSubtitle')} loading={isLoading} empty={!isLoading && !hasHealthData}>
            <DashboardPieChart data={healthPie} />
          </ChartCard>
        </GridItem>
      </GridContainer>
    </Stack>
  );
}
